import React, { useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { Organization } from '../../../models/organization';
import { IntelligenceProductStatus } from '../../../api/ai';
import styles from '../../settings/configuration/shared/IntelligencePanel.module.scss';
import IntelligenceCapacityCards from './IntelligenceCapacityCards';
import IntelligencePackCards from './IntelligencePackCards';
import IntelligencePendingVesBanner from './IntelligencePendingVesBanner';
import IntelligencePurchaseModal from './IntelligencePurchaseModal';
import { useIntelligenceHire } from './useIntelligenceHire';

export type IntelligenceActivateOfferVariant =
  | 'activate'
  | 'trialExhausted'
  | 'outOfCredits';

interface IntelligenceActivateOfferProps {
  organizationId: string;
  organization: Organization | null;
  status: IntelligenceProductStatus;
  onActivated: () => void | Promise<void>;
  variant?: IntelligenceActivateOfferVariant;
}

const LEADS: Record<IntelligenceActivateOfferVariant, string> = {
  activate:
    'Empieza con 14 días de Intelligence Plus o activa Lite, Plus o Extra desde aquí.',
  trialExhausted:
    'Se acabaron los créditos de la prueba. Activa Lite, Plus o Extra para continuar — durante la prueba no se pueden comprar paquetes.',
  outOfCredits:
    'Se agotaron los créditos de este periodo. Compra un paquete para seguir chateando, o cambia de capacidad.',
};

const IntelligenceActivateOffer: React.FC<IntelligenceActivateOfferProps> = ({
  organizationId,
  organization,
  status,
  onActivated,
  variant = 'activate',
}) => {
  const [notice, setNotice] = useState<string | null>(null);
  const hire = useIntelligenceHire({
    organizationId,
    organization,
    status,
    onSuccess: setNotice,
    onActivated,
  });

  const showPacks =
    variant === 'outOfCredits' &&
    Boolean(status.creditPacks && status.creditPacks.length > 0);

  return (
    <div className={styles.activateOffer}>
      {notice ? (
        <Alert
          variant="info"
          className="mb-3 py-2"
          dismissible
          onClose={() => setNotice(null)}
        >
          {notice}
        </Alert>
      ) : null}
      <IntelligencePendingVesBanner items={hire.pendingVes} />
      <p className={styles.activateLead}>{LEADS[variant]}</p>
      {variant === 'activate' && status.trialAvailable ? (
        <div className={styles.activateTrial}>
          <Button size="sm" variant="primary" onClick={hire.startTrial}>
            Probar Intelligence gratis (14 días)
          </Button>
        </div>
      ) : null}
      {showPacks && status.creditPacks ? (
        <IntelligencePackCards
          packs={status.creditPacks}
          pendingDescription={hire.pendingPack?.itemDescription}
          onSelect={hire.openPack}
        />
      ) : null}
      <IntelligenceCapacityCards
        status={status}
        pendingCapacity={Boolean(hire.pendingCapacity)}
        idleLabel={variant === 'trialExhausted' ? 'Suscribirse' : 'por mes'}
        onSelect={hire.openCapacity}
      />
      <IntelligencePurchaseModal hire={hire} />
    </div>
  );
};

export default IntelligenceActivateOffer;
